import { fromJS, Map, Set } from 'immutable';
import { LOCATION_CHANGE } from 'connected-react-router';

import { ORDERTYPE_STRING_MAP, ORDERTYPE_CODE_MAP } from './constants';
import { ORDER_PAYMENT_METHOD_OPTIONS, ORDER_TYPE_ATONCE_ALT } from '../shop/constants';
import { ORDER_STATUS_OPTIONS } from './orderReturns/constants';

import {
  requestOrders,
  addOrdersFilter,
  addOrderShippingAddress,
  removeOrdersFilter,
  clearOrdersFilters,
  updateOrdersDateRange,
  setOrdersFilters,
  updateSortBy,
  clearSortBy,
  resetPreviousLocation,
  requestOrderDetails,
  clearOrderDetails,
  setOrdersTableSearch,
  clearOrdersTableSearch,
  generateOrderTrackingReport,
  clearDownloadReportError
} from './actions';

export const defaultState = Map({
  accountOrders: [],
  isFetching: false,
  selectedFilters: Map({
    departmentNames: Set([]),
    orderStatus: Set([]),
    paymentMethod: Set([]),
    orderType: Set([])
  }),
  sortBy: { field: 'createTimestamp', order: 'DESC' },
  dateRange: { period: '24 months' },
  initialFilterOptions: [
    {
      key: 'orderType',
      values: Object.keys(ORDERTYPE_STRING_MAP).reduce((total, type) => {
        if (type !== ORDERTYPE_CODE_MAP[ORDER_TYPE_ATONCE_ALT]) {
          total.push({ value: type, label: ORDERTYPE_STRING_MAP[type] });
        }
        return total;
      }, [])
    },
    { key: 'paymentMethod', values: ORDER_PAYMENT_METHOD_OPTIONS },
    { key: 'orderStatus', values: ORDER_STATUS_OPTIONS }
  ],
  filterOptions: [],
  orderDetailsData: null,
  currentLocation: null,
  previousLocation: null,
  orderShippingAddress: [],
  ordersTableSearch: {},
  isGeneratingOrderTrackingReport: false,
  hasDownloadError: false
});

export const formatOption = options =>
  options.map(option => {
    const parts = option.split('_');
    return parts.length > 1 ? `${parts[0]} ${parts[1].toUpperCase()}` : parts[0];
  });

export const orderShippingAddressFormatted = data => {
  return [
    {
      addressLine1: data?.addressName?.value || data.name,
      addressLine2: data?.addressName2?.value || data.addressLine1,
      streetAddressLine1: data?.streetAddressLine1?.value || data.addressLine2,
      city: data?.cityName?.value || data.city,
      state: data?.provinceStateCode?.value || data.state,
      zipCode: data?.postalCode?.value || data.zipCode,
      country: data?.countryCode?.value || data.country
    }
  ];
};

export default function orders(state = defaultState, action) {
  const newState = fromJS(state);
  switch (action.type) {
    case requestOrders.event.start:
      return newState.set('isFetching', true);
    case requestOrders.event.success:
      return newState.withMutations(state => {
        state.set('accountOrders', action.payload).set('isFetching', false);
      });
    case requestOrders.event.error:
      return newState.withMutations(state => {
        state.set('accountOrders', []).set('isFetching', false);
      });
    case setOrdersFilters.type:
      return newState.set('filterOptions', [action.payload, ...newState.get('initialFilterOptions')]);
    case addOrdersFilter.type:
      return newState.setIn(
        ['selectedFilters', action.payload.key],
        Set(newState.getIn(['selectedFilters', action.payload.key]).add(action.payload.value))
      );
    case setOrdersTableSearch.type:
      return newState.set('ordersTableSearch', action.payload);
    case clearOrdersTableSearch.type:
      return newState.set('ordersTableSearch', {});
    case addOrderShippingAddress.type:
      return newState.set('orderShippingAddress', orderShippingAddressFormatted(action.payload));
    case removeOrdersFilter.type:
      return newState.setIn(
        ['selectedFilters', action.payload.key],
        Set(newState.getIn(['selectedFilters', action.payload.key]).delete(action.payload.value))
      );
    case clearOrdersFilters.type:
      return newState.set('selectedFilters', defaultState.get('selectedFilters'));
    case updateOrdersDateRange.type:
      return newState.set('dateRange', action.payload);
    case updateSortBy.type:
      return newState.set('sortBy', action.payload);
    case clearSortBy.type:
      return newState.set('sortBy', defaultState.get('sortBy'));
    case requestOrderDetails.event.success:
      return newState.set('orderDetailsData', action.payload);
    case clearOrderDetails.type:
      return newState.set('orderDetailsData', null);
    case LOCATION_CHANGE:
      return newState.withMutations(state => {
        state
          .set('previousLocation', newState.get('currentLocation'))
          .set('currentLocation', action.payload.location.pathname);
      });
    case resetPreviousLocation.type:
      return newState.set('previousLocation', '/orders');
    case generateOrderTrackingReport.event.start:
      return newState.set('isGeneratingOrderTrackingReport', true);
    case generateOrderTrackingReport.event.success:
      return newState.set('isGeneratingOrderTrackingReport', false);
    case generateOrderTrackingReport.event.error:
      return newState.set('isGeneratingOrderTrackingReport', false).set('hasDownloadError', true);
    case clearDownloadReportError.type:
      return newState.set('hasDownloadError', false);
    default:
      return newState;
  }
}

export const accountOrders = state => state.orders.get('accountOrders').orders;
export const isFetching = state => state.orders.get('isFetching');
export const selectedFilters = state => state.orders.get('selectedFilters');
export const dateRange = state => state.orders.get('dateRange');
export const filterOptions = state => state.orders.get('filterOptions');
export const sortBy = state => state.orders.get('sortBy');
export const orderDetailsData = state => state.orders.get('orderDetailsData');
export const ordersTableSearch = state => state.orders.get('ordersTableSearch');
export const previousLocationWasOrderDetails = state => /orders\/id/g.test(state.orders.get('previousLocation'));
export const previousLocationWasReturnStatus = state => {
  const prev = state.orders.get('previousLocation');
  if (/orders\/returns/g.test(prev)) {
    const prevSplit = prev.split('orders/returns');
    return prevSplit[prevSplit.length - 1] === '';
  }
  return false;
};
export const previousLocationWasReturnStep = state => /orders\/returns\/id/g.test(state.orders.get('previousLocation'));
export const previousLocationWasReturnDetails = state =>
  /orders\/returns\/details\/id/g.test(state.orders.get('previousLocation'));
export const currentLocation = state => state.orders.get('currentLocation');
export const isGeneratingOrderTrackingReport = state => state.orders.get('isGeneratingOrderTrackingReport');
export const hasDownloadError = state => state.orders.get('hasDownloadError');
